'use client';
import React, { useState, useEffect, useRef } from 'react';
import styles from "@/components/SearchBar/search-bar.module.css";
import Image from "next/image";
import DatePicker from "react-multi-date-picker";
import Link from "next/link";
import "react-multi-date-picker/styles/layouts/mobile.css";
import LocationDrop from "@/components/SearchBar/LocationDrop/LocationDrop";
import { setLocalStorage } from "@/utils/localStorage";
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

/**
 * @param view    true = the home page's hero bar, false = the slim "alt" bar.
 * @param compact stacks the cells into a single narrow column so the whole bar
 *                fits the search page's filter rail (and keeps the labels).
 */
export default function SearchBar({ view = true, urlData = {}, compact = false, filters }) {
    const todayStart = React.useMemo(() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t;
    }, []);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const formatTimestamps = (tsString) => {
        if (!tsString) return "";
        const parts = tsString.includes("+")
            ? tsString.split("+")
            : tsString.split(" ");
        return parts
            .map((ts) => {
                const d = new Date(Number(ts) * 1000);
                const dd = String(d.getUTCDate()).padStart(2, "0");
                const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
                const yyyy = d.getUTCFullYear();
                return `${dd}/${mm}/${yyyy}`;
            })
            .join(" ~ ");
    };

    const [dates, setDates] = useState([]);
    const [minDate, setMinDate] = useState(new Date());
    const [firstDateClicked, setFirstDateClicked] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const blockedDayRef = React.useRef({ date: null, element: null });
    const applyHoverStyles = React.useCallback((element, isActive) => {
        if (!element) return;
        const span = element.querySelector("span");
        if (!span) return;
        if (isActive) {
            span.style.color = "#ffffff";
            span.style.fontWeight = "600";
            span.style.opacity = "1";
            span.style.borderRadius = "0";
        } else {
            span.style.removeProperty("color");
            span.style.removeProperty("font-weight");
            span.style.removeProperty("opacity");
            span.style.removeProperty("border-radius");
        }
    }, []);

    const convertToJsDate = (value) => {
        if (!value) return null;
        let date;
        if (typeof value?.toDate === "function") {
            date = value.toDate();
        } else if (value instanceof Date) {
            date = new Date(value);
        } else if (typeof value === "object" && typeof value.year === "number") {
            const monthIndex = typeof value.month?.index === "number"
                ? value.month.index
                : (typeof value.month === "number" ? value.month - 1 : 0);
            date = new Date(value.year, monthIndex, value.day);
        } else {
            date = new Date(value);
        }
        if (!Number.isNaN(date.getTime())) {
            date.setHours(0, 0, 0, 0);
        }
        return date;
    };

    const normalizeValueToDates = (value) => {
        if (!Array.isArray(value)) return value;
        return value.map((item) => {
            if (!item) return item;
            return convertToJsDate(item);
        });
    };

    const enforceMinimumGap = (value) => {
        if (!Array.isArray(value) || value.length !== 2) return value;
        const [start, end] = value;
        if (!start || !end) return value;
        const diffInDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
        if (diffInDays <= 1) {
            const adjustedEnd = new Date(start);
            adjustedEnd.setDate(adjustedEnd.getDate() + 2);
            return [start, adjustedEnd];
        }
        return value;
    };

    const handleDateChange = (value) => {
        if (Array.isArray(value)) {
            const normalized = normalizeValueToDates(value);
            const adjusted = enforceMinimumGap(normalized);
            setDates(adjusted);
        } else {
            setDates(value);
        }
    };

    const blockedDate = React.useMemo(() => {
        const firstDate = convertToJsDate(firstDateClicked);
        if (!firstDate) return null;
        const blocked = new Date(firstDate);
        blocked.setDate(blocked.getDate() + 1);
        return blocked;
    }, [firstDateClicked]);

    const mapDays = React.useCallback(({ date }) => {
        const currentDate = convertToJsDate(date);
        const attrs = {};
        if (currentDate) {
            attrs.onMouseEnter = () => {
                const blocked = blockedDayRef.current;
                if (!blocked?.date || !blocked.element) return;
                if ((currentDate - blocked.date) / (1000 * 60 * 60 * 24) > 0) {
                    blocked.element.classList.add("rmdp-range-hover");
                    applyHoverStyles(blocked.element, true);
                } else {
                    blocked.element.classList.remove("rmdp-range-hover");
                    applyHoverStyles(blocked.element, false);
                }
            };
        }
        if (!blockedDate || !currentDate) return attrs;
        if (currentDate.getTime() === blockedDate.getTime()) {
            return {
                ...attrs,
                disabled: true,
                className: styles.disabledGapDay,
                ref: (node) => {
                    if (!node) return;
                    const parentDay = node.closest(".rmdp-day");
                    if (parentDay) {
                        parentDay.classList.add(styles.disabledGapDayParent);
                        const span = parentDay.querySelector("span");
                        span?.classList.add(styles.disabledGapDay);
                        blockedDayRef.current.element = parentDay;
                        blockedDayRef.current.date = blockedDate;
                    }
                },
            };
        }
        return attrs;
    }, [blockedDate, applyHoverStyles]);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const previous = blockedDayRef.current;
        if (previous?.element) {
            applyHoverStyles(previous.element, false);
            previous.element.classList.remove("rmdp-range-hover", styles.disabledGapDayParent);
            const prevSpan = previous.element.querySelector("span");
            prevSpan?.classList.remove(styles.disabledGapDay);
        }
        blockedDayRef.current = { date: blockedDate, element: null };
    }, [blockedDate, applyHoverStyles]);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const handleMouseMove = (event) => {
            const blocked = blockedDayRef.current;
            if (!blocked?.element) return;
            const target = event.target;
            if (!(target instanceof Element)) return;
            const calendar = target.closest(".rmdp-calendar");
            if (!calendar) {
                blocked.element.classList.remove("rmdp-range-hover");
                applyHoverStyles(blocked.element, false);
            }
        };
        document.addEventListener("mousemove", handleMouseMove);
        return () => document.removeEventListener("mousemove", handleMouseMove);
    }, [applyHoverStyles]);

    const [pickup, setPickup] = useState("tbilisi");
    const [dropoff, setDropoff] = useState("tbilisi");

    const [currency, setCurrency] = useState("");
    const pickupDropRef = useRef(null);
    const dropoffDropRef = useRef(null);
    const datePickerOpenRef = useRef(null);

    const getDefaultDateRange = React.useCallback(() => {
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 4);
        return [startDate, endDate];
    }, []);

    function getCurrencyFromUrlOrStorage() {
        let code = "";
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            code = params.get('currency');
        }
        if (!code) {
            try {
                const rec = JSON.parse(localStorage.getItem('recordings') || '{}');
                code = rec.currency;
            } catch {}
        }
        return code || "";
    }

    useEffect(() => {
        const rec = JSON.parse(localStorage.getItem('recordings')) || {};
        if (urlData.points) {
            const [p, d] = urlData.points.split(" ");
            setPickup(p);
            setDropoff(d);
        } else if (rec.points) {
            const [p, d] = rec.points.split(" ");
            setPickup(p);
            setDropoff(d);
        }
        if (urlData.dates) {
            setDates(urlData.dates);
        } else if (rec.dates) {
            setDates(rec.dates);
        } else {
            setDates(getDefaultDateRange());
        }
        setCurrency(getCurrencyFromUrlOrStorage());
    // eslint-disable-next-line
    }, [urlData.points, urlData.currency, urlData.dates, getDefaultDateRange]);

    useEffect(() => {
        if (pathname !== "/search") return;
        try {
            const shouldRefresh = sessionStorage.getItem("pr_refresh_search_results") === "1";
            if (!shouldRefresh) return;
            sessionStorage.removeItem("pr_refresh_search_results");
            router.refresh();
        } catch {
            // Ignore storage access errors in restricted environments.
        }
    }, [pathname, router]);

    useEffect(() => {
        const onStorageUpdate = () => {
            setCurrency(getCurrencyFromUrlOrStorage());
        };
        const onPopstate = () => {
            setCurrency(getCurrencyFromUrlOrStorage());
        };
        window.addEventListener('storageUpdate', onStorageUpdate);
        window.addEventListener('popstate', onPopstate);
        return () => {
            window.removeEventListener('storageUpdate', onStorageUpdate);
            window.removeEventListener('popstate', onPopstate);
        }
    }, []);

    function convertDatesToTimestamps(dateString) {
        return dateString
            .split(',')
            .map(ds => {
                const [d, m, y] = ds.split('/');
                return Math.floor(Date.UTC(Number(y), Number(m) - 1, Number(d)) / 1000);
            })
            .join('+');
    }

    let timestampString = "";
    if (Array.isArray(dates) && dates.length) {
        // Support react-multi-date-picker DateObject via .toDate()
        timestampString = dates
            .map(d => {
                const jsDate = (d && typeof d?.toDate === "function") ? d.toDate() : new Date(d);
                return Math.floor(Date.UTC(
                    jsDate.getFullYear(),
                    jsDate.getMonth(),
                    jsDate.getDate()
                ) / 1000);
            })
            .join("+");
    } else if (typeof dates === "string" && dates) {
        timestampString = dates.replace(/ /g, "+");
    } else if (urlData.dates) {
        timestampString = urlData.dates.replace(/ /g, "+");
    }

    useEffect(() => {
        const existing = JSON.parse(localStorage.getItem('recordings')) || {};
        const updated = {
            ...existing,
            points: `${pickup} ${dropoff}`,
            ...(currency ? { currency } : {}),
            ...urlData
        };
        setLocalStorage("recordings", JSON.stringify(updated));
        window.dispatchEvent(new Event('storageUpdate'));
    }, [pickup, dropoff, currency, urlData]);

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 992);
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    let searchUrl = `/search?points=${pickup}+${dropoff}&dates=${timestampString}`;
    if (currency) {
        searchUrl += `&currency=${currency}`;
    }
    // A landing's subject (type / category / model slug) rides along to /search;
    // deliberately not written into recordings.
    for (const k of ["type", "category", "model"]) {
        if (filters?.[k]) searchUrl += `&${k}=${encodeURIComponent(filters[k])}`;
    }

    useEffect(() => {
        setIsSearching(false);
    }, [searchUrl]);

    useEffect(() => {
        setIsSearching(false);
    }, [pathname, searchParams?.toString()]);

    return (
        <>
            {/* the mobile "tap to search" strip + hide-under-1125px behaviour belong
                to the standalone bar; in the rail the bar is always shown */}
            {!compact && <div className={`${!view ? styles.showMobileSearchBarInfo : ""}`}></div>}
            <div
                className={`${!view && !compact ? styles.hideSearchBar : ""} ${styles.searchBarWrapper} ${view ? styles.view : styles.alt} ${compact ? styles.compact : ""}`}
                data-scroll-target="search-bar"
            >
                <div className={styles.searchBar}>

                    <div
                        className={`${styles.location} ${styles.clickableCell}`}
                        onClick={() => pickupDropRef.current?.toggle?.()}
                    >
                        <div className={styles.locationHeading}>
                            Pick‑up location
                        </div>
                        <div className={styles.locationContent}>
                            <LocationDrop
                                ref={pickupDropRef}
                                current={pickup}
                                onSelectOption={setPickup}
                            />
                        </div>
                    </div>

                    <div
                        className={`${styles.location} ${styles.clickableCell}`}
                        onClick={() => dropoffDropRef.current?.toggle?.()}
                    >
                        <div className={styles.locationHeading}>
                            Return location
                        </div>
                        <div className={styles.locationContent}>
                            <LocationDrop
                                ref={dropoffDropRef}
                                current={dropoff}
                                onSelectOption={setDropoff}
                            />
                        </div>
                    </div>

                    <div
                        className={`${styles.location} ${styles.clickableCell}`}
                        onClick={(e) => {
                            if (e.target.closest(".rmdp-calendar")) return;
                            datePickerOpenRef.current?.();
                        }}
                    >
                        <div className={styles.locationHeading}>
                            Choose Trip dates
                        </div>
                        <div className={styles.locationContent}>
                            {console.log(new Date())}
                            <Image src="/assets/images/icons/calendar.svg" width={35} height={35} alt="Calendar" />
                            <DatePicker
                                value={dates}
                                onChange={handleDateChange}
                                onFocusedDateChange={(_, dateClicked) => {
                                    if (
                                        firstDateClicked &&
                                        dateClicked?.day === firstDateClicked.day &&
                                        dateClicked?.month.index === firstDateClicked.month.index &&
                                        dateClicked?.year === firstDateClicked.year
                                    ) {
                                        setDates([firstDateClicked]);
                                        return;
                                    }
                                    if (!firstDateClicked) {
                                        setFirstDateClicked(dateClicked);
                                        setMinDate(dateClicked);
                                    } else {
                                        setFirstDateClicked(null);
                                    }
                                }}
                                format="DD/MM/YYYY"
                                className="rmdp-mobile"
                                range
                                rangeHover
                                // two months won't fit the narrow rail
                                numberOfMonths={isMobile || compact ? 1 : 2}
                                // In the rail the calendar is trapped inside the
                                // filter column's stacking context (position:sticky
                                // always makes one), so the cards and header paint
                                // over it. Portalling lifts it out to <body>.
                                portal={compact}
                                minDate={todayStart}
                                mapDays={mapDays}
                                mobileLabels={{
                                    OK: "Accept",
                                    CANCEL: "Close",
                                }}
                                render={(value, openCalendar) => {
                                    datePickerOpenRef.current = openCalendar;
                                    return (
                                        <div
                                            className={styles['search-bar-inputs']}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openCalendar();
                                            }}
                                        >
                                            <span>
                                                {value
                                                    ? value
                                                    : urlData.dates
                                                        ? formatTimestamps(urlData.dates)
                                                        : typeof dates === 'string' && dates
                                                            ? formatTimestamps(dates)
                                                            : <span>Start Date - End Date</span>
                                                }
                                            </span>
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.location}>
                        <div className={styles.searchButton}>
                            {timestampString ? (
                                <Link
                                    href={searchUrl}
                                    className={`btn btn-main w-100 find-your-car-btn-gtag ${isSearching ? styles.searching : ""}`}
                                    aria-busy={isSearching}
                                    onClick={() => {
                                        if (isSearching) return;
                                        if (typeof window !== 'undefined') {
                                            const currentUrl = `${window.location.pathname}${window.location.search}`;
                                            if (currentUrl === searchUrl) {
                                                return;
                                            }
                                        }
                                        setIsSearching(true);
                                    }}
                                >
                                    {isSearching ? (
                                        <>
                                            <span className={styles.loader} aria-hidden="true"></span>
                                            <span>Loading...</span>
                                        </>
                                    ) : compact ? (
                                        // in the results rail the count is noise — the page
                                        // already says how many cars are available
                                        "SEARCH"
                                    ) : (
                                        <>
                                            SEARCH
                                            <br />
                                            From 250 Cars
                                        </>
                                    )}
                                </Link>
                            ) : (
                                <div className={`btn btn-main`}>Please select<br />locations and dates</div>
                            )}
                        </div>
                    </div>

                    {/* <div className={styles.searchBarBottom}>
                        <div className={styles.promoCode}>
                            <input type="checkbox" /> <div>I have a promo code</div>
                        </div>
                    </div> */}
                </div>
            </div>
        </>
    );
}
